import type { SendMessageOptions } from "@tellescope/ai-agent";
import type { Session } from "@tellescope/sdk";

/**
 * Creates a getCompletion function that wraps the Tellescope SDK's
 * ai_conversations.send_message endpoint.
 *
 * Accepts the base SendMessageOptions type from @tellescope/ai-agent and
 * translates it to the Tellescope SDK's format.
 *
 * @param session - Tellescope SDK session
 * @returns getCompletion function compatible with Agent interface
 *
 * @example
 * ```typescript
 * import { create_agent } from '@tellescope/ai-agent';
 * import { createGetCompletion } from '@tellescope/ai-agent-tellescope';
 *
 * const agent = create_agent({
 *   name: 'my-agent',
 *   selectionPrompt: 'Help with tasks',
 *   tools: [...],
 *   getCompletion: createGetCompletion(session),
 * });
 * ```
 */
export const createGetCompletion = (session: Session) => {
  return async (options: SendMessageOptions): Promise<{ response: string }> => {
    const { messages, prompt, type, orchestrationId, maxTokens } = options;

    // Call SDK's send_message with transformed options
    const result = await session.api.ai_conversations.send_message({
      messages: messages.map(({ role, text }) => ({ role, text })), // strip timestamp
      prompt,
      type,
      orchestrationId,
      maxTokens,
    });

    // Extract text response from LLM output
    // The API returns messages array with user message first, then assistant response
    // We need the assistant's response (last message) and it uses 'text' not 'content'
    const responseMessages = result?.ai_conversation?.messages || [];
    const assistantMessage = responseMessages.find((m: { role: string }) => m.role === "assistant");
    const response = assistantMessage?.text || "";

    return { response };
  };
};
